import React, { useState, useEffect } from 'react';
import {
  Eye, Search, CheckCircle, XCircle, X, Clock, Users, ShieldCheck, ShieldClose, Trash2
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getStatusStyle } from '../../../config/statusColors';
import {
  getRequests,
  updateRequestStatus,
  deleteRequest,
} from '../../../services/demoAccessService';

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-gray-100 text-gray-600',
    amber: 'bg-gray-100 text-gray-600',
    green: 'bg-gray-100 text-gray-600',
    red: 'bg-gray-100 text-gray-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colors[color]}`}><Icon className="w-6 h-6" /></div>
      </div>
    </div>
  );
};

const DemoAccessManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => { loadRequests(); }, []);

  const loadRequests = () => {
    setRequests(getRequests());
  };

  const handleApprove = (id) => {
    updateRequestStatus(id, 'approved', user?.name || 'Admin', adminNotes);
    setAdminNotes('');
    setSelectedRequest(null);
    loadRequests();
  };

  const handleDeny = (id) => {
    updateRequestStatus(id, 'denied', user?.name || 'Admin', adminNotes);
    setAdminNotes('');
    setSelectedRequest(null);
    loadRequests();
  };

  const handleRevoke = (id) => {
    if (!window.confirm('Revoke demo access for this user?')) return;
    updateRequestStatus(id, 'denied', user?.name || 'Admin', 'Access revoked');
    loadRequests();
  };

  const handleDelete = (id) => {
    if (!window.confirm('Permanently delete this request?')) return;
    deleteRequest(id);
    setSelectedRequest(null);
    loadRequests();
  };

  const filtered = requests.filter(r => {
    const matchesSearch = !searchQuery ||
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied: requests.filter(r => r.status === 'denied').length,
  };

  const getStatusBadge = (status) => {
    const style = getStatusStyle(status);
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium capitalize" style={style}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Demo Access Management</h1>
        <p className="text-gray-600 mt-1">Review and manage demo access requests</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Requests" value={counts.total} icon={Users} color="blue" />
        <StatCard title="Pending" value={counts.pending} icon={Clock} color="amber" />
        <StatCard title="Approved" value={counts.approved} icon={ShieldCheck} color="green" />
        <StatCard title="Denied" value={counts.denied} icon={ShieldClose} color="red" />
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'denied'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                statusFilter === s ? 'bg-gray-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              {s} {s !== 'all' ? `(${counts[s]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  {requests.length === 0 ? (
                    <div>
                      <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No demo access requests yet.</p>
                    </div>
                  ) : (
                    'No requests match your filters.'
                  )}
                </td>
              </tr>
            ) : filtered.map(req => (
              <tr
                key={req.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => { setSelectedRequest(req); setAdminNotes(req.notes || ''); }}
              >
                <td className="px-4 py-4">
                  <p className="font-medium text-gray-900">{req.name}</p>
                </td>
                <td className="px-4 py-4 text-gray-600 text-sm">{req.email}</td>
                <td className="px-4 py-4 text-gray-600 text-sm">{req.company}</td>
                <td className="px-4 py-4 text-gray-600 text-sm">{req.role}</td>
                <td className="px-4 py-4">{getStatusBadge(req.status)}</td>
                <td className="px-4 py-4 text-gray-600 text-sm">{new Date(req.requestedAt).toLocaleDateString()}</td>
                <td className="px-4 py-4">
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    {req.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(req.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Approve"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeny(req.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Deny"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {req.status === 'approved' && (
                      <button
                        onClick={() => handleRevoke(req.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Revoke access"
                      >
                        <ShieldClose className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(req.id)}
                      className="p-1.5 text-gray-400 hover:bg-gray-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <Modal isOpen={!!selectedRequest} onClose={() => setSelectedRequest(null)} title="Request Details">
        {selectedRequest && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{selectedRequest.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{selectedRequest.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Company</p>
                <p className="font-medium text-gray-900">{selectedRequest.company}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Role / Title</p>
                <p className="font-medium text-gray-900">{selectedRequest.role}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-gray-500">Reason for Access</p>
                <p className="font-medium text-gray-900">{selectedRequest.reason}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                {getStatusBadge(selectedRequest.status)}
              </div>
              <div>
                <p className="text-sm text-gray-500">Requested</p>
                <p className="font-medium text-gray-900">{new Date(selectedRequest.requestedAt).toLocaleString()}</p>
              </div>
              {selectedRequest.reviewedAt && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Reviewed At</p>
                    <p className="font-medium text-gray-900">{new Date(selectedRequest.reviewedAt).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Reviewed By</p>
                    <p className="font-medium text-gray-900">{selectedRequest.reviewedBy}</p>
                  </div>
                </>
              )}
            </div>

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 resize-none"
                placeholder="Add notes about this request..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              {selectedRequest.status === 'pending' && (
                <>
                  <button
                    onClick={() => handleApprove(selectedRequest.id)}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => handleDeny(selectedRequest.id)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" /> Deny
                  </button>
                </>
              )}
              {selectedRequest.status === 'approved' && (
                <button
                  onClick={() => handleRevoke(selectedRequest.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <ShieldClose className="w-4 h-4" /> Revoke Access
                </button>
              )}
              <button
                onClick={() => setSelectedRequest(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DemoAccessManagement;
